if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      // small helper copied from VAT logic: read cookie
      _getCookie(name) {
        const nameEQ = name + '=';
        const ca = (document.cookie || '').split(';');
        for (let i = 0; i < ca.length; i++) {
          let c = ca[i].trim();
          if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
        }
        return null;
      }

      // local fallback that toggles visible VAT spans if vatHelpers not present
      _localVatFallback(root) {
        try {
          if (!root) return;
          const cookie = this._getCookie('vat_display');
          const INC = 'inc', EXC = 'exc';
          const mode = cookie || (() => {
            const f = root.querySelector('.f-price');
            if (!f) return EXC;
            const inc = f.querySelector('.f-price--inc');
            return (inc && window.getComputedStyle(inc).display !== 'none') ? INC : EXC;
          })();

          // apply per f-price block
          root.querySelectorAll('.f-price').forEach(wrap => {
            const incSpans = wrap.querySelectorAll('.f-price--inc');
            const excSpans = wrap.querySelectorAll('.f-price--exc');
            if (mode === INC) {
              incSpans.forEach(s => { s.style.display = ''; });
              excSpans.forEach(s => { s.style.display = 'none'; });
            } else {
              incSpans.forEach(s => { s.style.display = 'none'; });
              excSpans.forEach(s => { s.style.display = ''; });
            }

            const labelInc = wrap.querySelector('.vat-label--inc');
            const labelExc = wrap.querySelector('.vat-label--exc');
            if (labelInc && labelExc) {
              if (mode === INC) { labelInc.style.display = ''; labelExc.style.display = 'none'; }
              else { labelInc.style.display = 'none'; labelExc.style.display = ''; }
            }

            const unitInc = wrap.querySelector('[data-unit-price-inc]');
            const unitExc = wrap.querySelector('[data-unit-price-exc]');
            if (unitInc && unitExc) {
              if (mode === INC) { unitInc.style.display = ''; unitExc.style.display = 'none'; }
              else { unitInc.style.display = 'none'; unitExc.style.display = ''; }
            }
          });
        } catch (e) {
          // console.warn('[VAT][recommendations] local fallback failed', e);
        }
      }

      // call vat helpers / dispatch event / fallback with retries
      _applyVat(root) {
        try {
          if (!root) return;
          // console.log('[VAT][recommendations] apply VAT to recommendations root', root);

          // preferred: vatHelpers
          try {
            if (window.vatHelpers && typeof window.vatHelpers.applyVatModeToNode === 'function') {
              // console.log('[VAT][recommendations] calling vatHelpers.applyVatModeToNode');
              window.vatHelpers.applyVatModeToNode(root);
            }
          } catch (e) {
            // console.warn('[VAT][recommendations] vatHelpers threw', e);
          }

          // dispatch event for other listeners
          try {
            document.dispatchEvent(new CustomEvent('vat:apply', { detail: { root } }));
          } catch (e) {
            console.warn('[VAT][recommendations] dispatch vat:apply failed', e);
          }

          // local fallback (immediate)
          this._localVatFallback(root);

          // retry after microtask + timeout for nodes hydrated later
          Promise.resolve().then(() => {
            try { window.vatHelpers?.applyVatModeToNode?.(root); } catch(e) {}
          });
          setTimeout(() => {
            try { window.vatHelpers?.applyVatModeToNode?.(root); } catch(e) {}
            // also re-run local fallback in case something mutated it back
            this._localVatFallback(root);
          }, 160);
        } catch (err) {
          // console.warn('[VAT][recommendations] apply error', err);
        }
      }

      // observe added nodes under recommendations to re-apply VAT
      _observeForVat(root) {
        if (!root) return;
        try {
          // avoid multiple observers
          if (this._vatObserver) return;
          this._vatObserver = new MutationObserver((mutations) => {
            let need = false;
            for (const m of mutations) {
              if (m.addedNodes && m.addedNodes.length) {
                // if any .f-price added or product-card added — reapply
                for (const n of m.addedNodes) {
                  if (n.nodeType !== 1) continue;
                  if (n.matches && n.matches('.f-price, .product-card') ) { need = true; break; }
                  if (n.querySelector && (n.querySelector('.f-price') || n.querySelector('.product-card'))) { need = true; break; }
                }
              }
            }
            if (need) {
              // console.log('[VAT][recommendations] observer triggered re-apply');
              this._applyVat(root);
            }
          });
          this._vatObserver.observe(root, { childList: true, subtree: true });
        } catch (e) {
          // console.warn('[VAT][recommendations] _observeForVat failed', e);
        }
      }

      init() {
        // original fetch logic
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');

            if (sectionInnerHTML === null) return;

            const recommendations = sectionInnerHTML.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;

              // new: apply VAT immediately to the local inserted recommendations node
              // prefer to look for the block we inserted, but fallback to this
              const root = this.querySelector('.products-grid-wrap') || this;
              // console.log('[VAT][recommendations] recommendations injected — applying VAT to', root);
              this._applyVat(root);
              this._observeForVat(root);

              this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            } else {
              if (recommendations && recommendations.classList.contains('complementary-products')) {
                this.closest('.product__block')?.remove();
              } else {
                this.closest('.shopify-section')?.remove();
              }
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    }
  );
}
